import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { useGetForeignCurrencyRates } from '@/context/services/foreign_currency/Detail/ApCurrencyRates.service';
import { Container } from '@/features/foreign_currency_settings/components/Container/Container';
import { CurrencyRateTable } from '@/features/foreign_currency_settings/components/Detail/CurrencyRateTable';
import { DataTableCurrencyRate } from '@/features/foreign_currency_settings/components/Detail/CurrencyRateTable/type';
import { useTranslation } from '@/i18n';
import { format, Heading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { DATE_FORMAT } from 'date-util';
import { memo, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from '../foreignCurrencySettings.module.scss';

const cx = classnames.bind(styles);

export const ForeignCurrencySettingsDetail = memo(() => {
  const { currency } = useParams();

  const { t } = useTranslation();
  const [query, setQuery] = useState({ page: 1 });
  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    // onClearMessage,
  ] = useErrorInlineNotification();

  const onPageChange = useCallback(
    (page: number) => {
      setQuery({ ...query, page });
    },
    [query]
  );

  const {
    data: apCurrencyRates,
    refetch,
    pagination: _pagination,
    isLoading,
  } = useGetForeignCurrencyRates({
    currency,
    page: query.page,
    onPageChange,
  });

  const pagination = useMemo(() => {
    return {
      ..._pagination,
      showPerPage: false,
    };
  }, [_pagination]);

  const dataTableCurrencyRates: DataTableCurrencyRate[] =
    apCurrencyRates?.map((rate) => ({
      id: rate.id ?? '',
      rate: rate.value ?? '',
      start_date: format(rate.start_date, DATE_FORMAT) ?? '',
      end_date: format(rate.end_date, DATE_FORMAT) ?? '',
      period: `${format(rate.start_date, DATE_FORMAT)} 〜 ${format(
        rate.end_date,
        DATE_FORMAT
      )}`,
      updated_at: format(rate.updated_at, DATE_FORMAT) ?? '',
    })) ?? [];

  const onCallbackRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <ErrorInlineNotificationProvider value={setError}>
      <Container>
        <div className={cx(styles['inline-notification-wrap'])}>
          {ErrorContextHolder}
        </div>
        <Heading as='h3' className={cx(styles['page-heading'])}>
          {t('foreign_currency_settings.detail.title')}
        </Heading>
        <CurrencyRateTable
          currency={currency}
          data={dataTableCurrencyRates}
          pagination={pagination}
          onCallbackRefetch={onCallbackRefetch}
          isLoading={isLoading}
        />
      </Container>
    </ErrorInlineNotificationProvider>
  );
});

ForeignCurrencySettingsDetail.displayName = 'ForeignCurrencySettingsDetail';
